import { addIsoDays, MAX_DELIVERY_OFFSET, offsetLabel, WEEKDAYS_ES } from "@probetalab/contracts";
import { Field, Input, Segmented, Select } from "./ui.js";

/**
 * The study's delivery rule in its ficha (ADR 0027). Four ways, as the counter says them:
 * no promised date, the same day, a fixed number of days, or by the day the sample arrives (the
 * culture note: "Lunes → Jueves … Domingo → Jueves"). The weekday table edits one row per
 * receipt day, each choosing the delivery day by name; offsets are what is stored.
 */

export interface DeliveryRuleValue {
  delivery_days: number | null;
  delivery_by_weekday: number[] | null;
}

export type DeliveryMode = "none" | "same" | "days" | "weekday";

export function modeOf(v: DeliveryRuleValue): DeliveryMode {
  if (v.delivery_by_weekday) return "weekday";
  if (v.delivery_days === null) return "none";
  return v.delivery_days === 0 ? "same" : "days";
}

/** The value after switching mode, carrying over what makes sense. */
export function switchMode(v: DeliveryRuleValue, mode: DeliveryMode): DeliveryRuleValue {
  switch (mode) {
    case "none":
      return { delivery_days: null, delivery_by_weekday: null };
    case "same":
      return { delivery_days: 0, delivery_by_weekday: null };
    case "days":
      return { delivery_days: v.delivery_days && v.delivery_days > 0 ? v.delivery_days : 1, delivery_by_weekday: null };
    case "weekday": {
      const base = Math.min(MAX_DELIVERY_OFFSET, v.delivery_days ?? 0);
      return { delivery_days: null, delivery_by_weekday: v.delivery_by_weekday ?? Array.from({ length: 7 }, () => base) };
    }
  }
}

const OFFSETS = Array.from({ length: MAX_DELIVERY_OFFSET + 1 }, (_, i) => i);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A Monday of reference, to say "(+3 días)" next to a weekday name in the select. */
const MONDAY = "2026-11-02";

export function DeliveryRuleField({ value, onChange, daysText, onDaysText }: { value: DeliveryRuleValue; onChange: (v: DeliveryRuleValue) => void; daysText: string; onDaysText: (t: string) => void }) {
  const mode = modeOf(value);
  const daysOk = deliveryRuleValid(mode, daysText);
  return (
    <section className="stack delivery-rule" style={{ gap: 10 }} aria-label="Entrega de resultados">
      <div>
        <div className="kicker">Entrega de resultados</div>
        <p className="small muted" style={{ margin: "4px 0 0" }}>
          Con esto se calcula la fecha de entrega de cada solicitud y aparece en el Calendario, el ticket y la cotización por WhatsApp.
        </p>
      </div>
      <Segmented<DeliveryMode>
        value={mode}
        onChange={(m) => {
          const next = switchMode(value, m);
          if (m === "days") onDaysText(String(next.delivery_days));
          onChange(next);
        }}
        options={[
          { value: "none", label: "Sin fecha" },
          { value: "same", label: "Mismo día" },
          { value: "days", label: "Días fijos" },
          { value: "weekday", label: "Según el día" },
        ]}
      />
      {mode === "days" && (
        <Field label="Días después de recibir la muestra" error={daysOk ? null : "Escribe un número de días (1 o más)."}>
          <Input
            aria-label="Días de entrega"
            inputMode="numeric"
            value={daysText}
            style={{ maxWidth: 140 }}
            onChange={(e) => {
              onDaysText(e.target.value);
              const t = e.target.value.trim();
              if (/^\d{1,3}$/.test(t) && Number(t) > 0) onChange({ delivery_days: Number(t), delivery_by_weekday: null });
            }}
          />
        </Field>
      )}
      {mode === "weekday" && value.delivery_by_weekday && (
        <div className="weekday-table" role="table" aria-label="Entrega según el día en que llega la muestra">
          <div className="wt-head" role="row">
            <span role="columnheader">Si la muestra llega el</span>
            <span role="columnheader">se entrega el</span>
          </div>
          {WEEKDAYS_ES.map((day, i) => (
            <div key={day} className="wt-row" role="row">
              <span role="cell" className="wt-day">{cap(day)}</span>
              <span role="cell">
                <Select
                  aria-label={`Entrega si llega el ${day}`}
                  value={String(value.delivery_by_weekday![i])}
                  onChange={(e) => {
                    const t = [...value.delivery_by_weekday!];
                    t[i] = Number(e.target.value);
                    onChange({ delivery_days: null, delivery_by_weekday: t });
                  }}
                >
                  {OFFSETS.map((o) => (
                    <option key={o} value={o}>
                      {offsetLabel(i, o)}
                      {o > 0 ? ` (+${o} ${o === 1 ? "día" : "días"})` : ""}
                    </option>
                  ))}
                </Select>
              </span>
            </div>
          ))}
          <p className="small muted" style={{ margin: 0 }}>{exampleText(value.delivery_by_weekday)}</p>
        </div>
      )}
    </section>
  );
}

const longDate = new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long" });

/** "Ejemplo: si llega el lunes 2 de noviembre, se entrega el jueves 5 de noviembre." */
export function exampleText(table: readonly number[]): string {
  const due = addIsoDays(MONDAY, table[0] ?? 0);
  const fmt = (iso: string) => longDate.format(new Date(`${iso}T12:00:00Z`)).replace(",", "");
  return due === MONDAY ? `Ejemplo: si llega el ${fmt(MONDAY)}, se entrega ese mismo día.` : `Ejemplo: si llega el ${fmt(MONDAY)}, se entrega el ${fmt(due)}.`;
}

/** The days text is valid for the mode (only "Días fijos" has free text). */
export function deliveryRuleValid(mode: DeliveryMode, daysText: string): boolean {
  if (mode !== "days") return true;
  const t = daysText.trim();
  return /^\d{1,3}$/.test(t) && Number(t) > 0;
}
